// ============================================================================
// WEDDING ANNIVERSARY CELEBRATIONS · MASTER REGISTRY
// ============================================================================
// Five anniversary milestones × three package tiers = 15 curated combinations.
// Every package shares the same twelve inclusions (below) with tier-specific
// upgrades. Nine optional add-ons available across all packages.
// ============================================================================

const IMG = {
  hero:      "https://images.unsplash.com/photo-1520854221256-17451cc331bf?auto=format&fit=crop&w=2000&q=80",
  first:     "https://images.unsplash.com/photo-1591780980986-5c8bcbb6f27b?auto=format&fit=crop&w=1600&q=85",
  decade:    "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1600&q=85",
  silver:    "https://images.unsplash.com/photo-1594736797933-d0501ba2fe65?auto=format&fit=crop&w=1600&q=85",
  golden:    "https://images.unsplash.com/photo-1544198365-f5d60b6d8190?auto=format&fit=crop&w=1600&q=85",
  diamond:   "https://images.unsplash.com/photo-1465495976277-4387d4b0e4a6?auto=format&fit=crop&w=1600&q=85",
  temple:    "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?auto=format&fit=crop&w=1600&q=85",
  candles:   "https://images.unsplash.com/photo-1520854221256-17451cc331bf?auto=format&fit=crop&w=1600&q=85",
  petals:    "https://images.unsplash.com/photo-1591780980986-5c8bcbb6f27b?auto=format&fit=crop&w=1600&q=85",
  himalaya:  "https://images.pexels.com/photos/14149541/pexels-photo-14149541.jpeg?auto=compress&cs=tinysrgb&w=1600&q=85",
};

export const ANNIVERSARY_HERO_IMAGE = IMG.hero;


// ----- The 5 Anniversary Categories ------------------------------------------
export const ANNIVERSARY_TYPES = [
  {
    id: "first",
    icon: "flower",
    symbol: "🌸",
    name: "First Wedding Anniversary",
    subtitle: "The First Year",
    tagline: "The vow, still fresh · The blessing, doubled",
    image: IMG.first,
    accent: "rose",
    story: "One year in — return to Triyuginarayan Temple, receive the blessing of Vivah-Sthali, and mark the beginning of a lifetime.",
    blessing: "A shorter Vivah-Anushthan puja beside the Akhand Dhuni fire, presided by a KC-approved pandit. Marigold offerings and the sacred ash for both partners.",
    highlights: [
      "Special first-year Vivah-Anushthan puja",
      "Fresh rose-petal room décor on arrival",
      "Couple photoshoot at the temple courtyard",
      "Anniversary cake baked in-house by KC kitchen",
    ],
  },
  {
    id: "decade",
    icon: "sparkles",
    symbol: "✨",
    name: "Decade Wedding Anniversary",
    subtitle: "10 Years · The Foundation Anniversary",
    tagline: "A decade of shared prayers · A decade of shared prasadam",
    image: IMG.decade,
    accent: "amber",
    story: "Ten years is when a marriage becomes a family. Return to the shrine that witnessed the beginning, with the children (or the years) you've grown together.",
    blessing: "Full Vivah-Anushthan with Akhand Dhuni offerings, followed by a family aarti at the temple. Optional inclusion of children in the ceremony.",
    highlights: [
      "Full Vivah-Anushthan ceremony + family aarti",
      "Signature copper-and-bloom room décor",
      "Family + couple photoshoot",
      "Anniversary cake with photo-print topper",
    ],
  },
  {
    id: "silver",
    icon: "moon",
    symbol: "💎",
    name: "Silver Wedding Anniversary",
    subtitle: "25 Years · The Silver Milestone",
    tagline: "Twenty-five winters · Twenty-five springs · One vow",
    image: IMG.silver,
    accent: "silver",
    story: "Twenty-five years of showing up for each other deserves a celebration that feels like a second wedding — with the same sanctity, but with the wisdom of everything you've built together.",
    blessing: "Grand Vivah-Anushthan ceremony with re-affirmation of vows before the Akhand Dhuni. Traditional silver-thread garlands and ceremonial Ashirwad from the pandit and family elders.",
    highlights: [
      "Grand ceremony with vow re-affirmation",
      "Silver-thread garland exchange",
      "Meadow candlelight dinner for the couple",
      "Signature cinematic film + photo book",
    ],
  },
  {
    id: "golden",
    icon: "crown",
    symbol: "👑",
    name: "Golden Wedding Anniversary",
    subtitle: "50 Years · The Golden Milestone",
    tagline: "Fifty years of grace · The pheras of a lifetime",
    image: IMG.golden,
    accent: "gold",
    story: "Fifty years is not a milestone. It is a monument. Return to the shrine that has waited for you, with the family that owes you everything.",
    blessing: "Royal Vivah-Anushthan with Poorna-Aahuti offerings and Akhand Dhuni prasadam distribution to all family members. Family Ashirwad ceremony with silver ceremonial plate.",
    highlights: [
      "Royal Vivah-Anushthan · Poorna-Aahuti",
      "Golden marigold cascade décor",
      "Full extended-family portrait session",
      "Signature film + printed legacy album",
    ],
  },
  {
    id: "diamond",
    icon: "diamond",
    symbol: "💠",
    name: "Diamond Wedding Anniversary",
    subtitle: "60 Years · The Diamond Milestone",
    tagline: "Rare · Timeless · Held with the utmost reverence",
    image: IMG.diamond,
    accent: "royal",
    story: "Sixty years is the highest blessing life can offer. Every moment of this day is designed as tribute — to the two of you, to the family you've raised, and to the sanctity that has held you.",
    blessing: "The most sacred ceremony we offer — Poorna-Vivah-Anushthan with Char Dham prasadam and lifetime-legacy Ashirwad. Four generations of the family included where possible.",
    highlights: [
      "Poorna-Vivah-Anushthan · Char Dham prasadam",
      "Themed royal décor · full production",
      "Four-generation portrait + heritage film",
      "Private helicopter option for the couple",
    ],
  },
];

// ----- The 3 Package Tiers (common structure across all anniversaries) -------
export const ANNIVERSARY_PACKAGES = [
  {
    id: "premium",
    name: "Premium Package",
    symbol: "⭐",
    guestMax: 20,
    guestLabel: "Up to 20 Guests",
    startingFrom: 79999,
    duration: "1 night · 2 days",
    tagline: "Intimate · Sacred · Beautifully held",
    accent: "amber",
    perks: [
      "One night at Kedarnath Cottage boutique suite",
      "Basic floral décor with rose accents",
      "Celebration dinner for up to 20 guests · pahadi thali",
      "Photo coverage · 200 edited photographs delivered",
      "1-minute anniversary reel for social sharing",
    ],
  },
  {
    id: "silver",
    name: "Silver Package",
    symbol: "🥈",
    guestMax: 30,
    guestLabel: "Up to 30 Guests",
    startingFrom: 149999,
    duration: "2 nights · 3 days",
    tagline: "Elevated · Signature · Cinematic",
    accent: "gold",
    featured: true,
    perks: [
      "Two nights at Kedarnath Cottage premium riverside rooms",
      "Signature copper-and-bloom décor",
      "Celebration lunch + candlelight dinner (up to 30 guests)",
      "Cinematic photo + video coverage · 3-min highlight film",
      "Couple's spa hour + welcome hamper",
    ],
  },
  {
    id: "diamond",
    name: "Diamond Package",
    symbol: "💎",
    guestMax: 50,
    guestLabel: "Up to 50 Guests",
    startingFrom: 249999,
    duration: "3 nights · 4 days",
    tagline: "Royal · Themed · Legacy production",
    accent: "royal",
    perks: [
      "Three nights at KC Signature Suites + partner luxury rooms",
      "Royal marigold cascade + themed candlelight arrangement",
      "Multi-cuisine celebration lunch + gala dinner (up to 50 guests)",
      "Full cinematic film · same-day edit + printed heritage album",
      "Helicopter arrival option + private meadow candlelight dinner",
    ],
  },
];

// ----- Universal inclusions (every package includes these 12) ----------------
export const ANNIVERSARY_INCLUDES = [
  { key: "blessing",     icon: "temple",  label: "Temple Blessing at Triyuginarayan" },
  { key: "puja",         icon: "flame",   label: "Special Vivah-Anushthan Puja Arrangement" },
  { key: "decor",        icon: "flower",  label: "Floral Décor · Rose · Marigold · Candles" },
  { key: "hotel",        icon: "bed",     label: "Premium Hotel Stay (tier-based)" },
  { key: "meal",         icon: "utensils",label: "Celebration Lunch / Dinner" },
  { key: "cake",         icon: "cake",    label: "Signature Anniversary Cake · In-House" },
  { key: "photo",        icon: "camera",  label: "Photography · Edited Album" },
  { key: "video",        icon: "video",   label: "Cinematic Videography · Highlight Reel" },
  { key: "shoot",        icon: "heart",   label: "Couple Photoshoot at Temple Courtyard" },
  { key: "transport",    icon: "car",     label: "Local Transportation Included" },
  { key: "gift",         icon: "gift",    label: "Curated Welcome Gift · KC Signature" },
  { key: "coordinator",  icon: "user",    label: "Dedicated Celebration Coordinator" },
];

// ----- Optional Add-ons (9 · shared across all tiers) ------------------------
export const ANNIVERSARY_ADDONS = [
  { key: "luxury_upgrade",    label: "Luxury Room Upgrade",             price: 15000 },
  { key: "romantic_decor",    label: "Romantic Room Décor · Petals + Candles", price: 8500 },
  { key: "live_music",        label: "Live Music · Classical / Sufi",   price: 35000 },
  { key: "drone_photo",       label: "Drone Photography",               price: 25000 },
  { key: "makeup",            label: "Professional Bridal Makeup",      price: 21000 },
  { key: "candlelight",       label: "Private Candlelight Dinner",      price: 12500 },
  { key: "helicopter",        label: "Helicopter Assistance for Couple",price: 285000 },
  { key: "sightseeing",       label: "Family Sightseeing Package",      price: 18500 },
  { key: "custom",            label: "Fully Customized Celebration",    price: null },
];

// ----- Lookups ---------------------------------------------------------------
export const getAnniversary = (id) => ANNIVERSARY_TYPES.find(a => a.id === id);
export const getPackage     = (id) => ANNIVERSARY_PACKAGES.find(p => p.id === id);

// ----- Currency --------------------------------------------------------------
export const inr = (n) => {
  if (n == null) return "On Request";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2).replace(/\.?0+$/, "")} Cr`;
  if (n >=   100000) return `₹${(n /   100000).toFixed(2).replace(/\.?0+$/, "")} L`;
  return `₹${n.toLocaleString("en-IN")}`;
};
